import type { HelpContent } from './types';

export const profitabilityReportHelp: HelpContent = {
  pageTitle: 'Profitability Report',
  description: 'Analyse profitability across your project portfolio with revenue, cost, gross profit, and margin data — including variance against budget.',

  functionality: {
    overview: 'The Profitability Report provides a detailed view of financial performance across your portfolio. It shows revenue, costs, gross profit, margin, and variance against budgeted profitability for each project, helping you understand where value is being created and where it is being eroded.',

    keyFeatures: [
      {
        label: 'Portfolio profitability summary',
        detail: 'KPI cards show total revenue, total costs, overall gross profit, and portfolio margin at a glance.',
      },
      {
        label: 'Margin variance against budget',
        detail: 'See how actual margin compares to budgeted margin for each project, with variance shown in both value and percentage points.',
      },
      {
        label: 'Project detail panel',
        detail: 'Click any project to open a side panel with a deeper breakdown of revenue, costs, and margin for that project.',
      },
      {
        label: 'GP calculation method',
        detail: 'The GP calculation method applied to the report is configurable in Settings, ensuring the report aligns with your organisation\'s financial methodology.',
      },
      {
        label: 'Search and filter',
        detail: 'Search by project name or client, and filter by profitability health to focus on specific projects.',
      },
      {
        label: 'Export to Excel',
        detail: 'Export the profitability report to Excel for use in financial reporting or board packs.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'KPI summary cards',
        description: 'Displays portfolio-level totals: total revenue, total costs, gross profit, and overall margin percentage.',
      },
      {
        name: 'GP method indicator',
        description: 'Shows which GP calculation method is applied to the report. Configurable in Settings by an Administrator.',
      },
      {
        name: 'Search bar',
        description: 'Filter the project list in real time by project name or client name.',
      },
      {
        name: 'Profitability health filter',
        description: 'Filter projects by their profitability health status to focus on projects with margin concerns.',
      },
      {
        name: 'Profitability table',
        description: 'Lists all projects with columns for project name, client, project manager, revenue, costs, gross profit, gross margin percentage, budget margin, margin variance, and profitability health.',
      },
      {
        name: 'Margin variance',
        description: 'The difference between actual gross margin and the budgeted gross margin, shown in both value and percentage points.',
      },
      {
        name: 'Profitability health indicator',
        description: 'A colour-coded indicator showing whether the project\'s profitability is On Track, At Risk, or Critical based on configured margin thresholds.',
      },
      {
        name: 'Project detail panel',
        description: 'A side panel that opens when you click a project row. Provides a deeper breakdown of the project\'s revenue, costs, and margin components.',
      },
      {
        name: 'Export button',
        description: 'Downloads the profitability report as an Excel file.',
      },
    ],

    useCases: [
      'Reviewing portfolio profitability ahead of a finance or board meeting.',
      'Identifying projects with low, negative, or declining margins.',
      'Comparing actual margin against budgeted margin to assess financial delivery.',
      'Drilling into a specific project\'s financial breakdown via the detail panel.',
      'Exporting profitability data for financial reporting or investor packs.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Profitability Report loads with all projects that have financial data. Use the KPI cards for a portfolio summary, then use the filters or click individual projects to explore detail.',
    steps: [
      {
        title: 'How do I identify projects with poor profitability?',
        items: [
          { text: 'Use the profitability health filter to focus on At Risk or Critical projects.' },
          { text: 'Look for projects with a red or amber profitability health indicator.' },
          { text: 'Review the margin variance column to understand how far actual margin is from budget.' },
        ],
      },
      {
        title: 'How do I see a detailed breakdown for a project?',
        items: [
          { text: 'Click any project row to open the project detail panel on the right.' },
          { text: 'The panel shows a breakdown of revenue, costs, and margin components for that project.' },
          { text: 'Click "View Project" in the panel to navigate to the full Project Details page.' },
        ],
      },
      {
        title: 'How do I export the report?',
        items: [
          { text: 'Apply any filters you need.' },
          { text: 'Click the Export button in the page header.' },
          { text: 'The report downloads as an Excel file.' },
        ],
      },
    ],
    proTips: [
      'Use this report alongside the WIP Report to connect profitability performance with outstanding billing — a project may be profitable on paper but have significant unbilled WIP.',
      'The margin variance column is the quickest way to spot projects that are profitable but underperforming against their financial plan.',
    ],
  },

  faq: [
    {
      question: 'What is the difference between the P&L Report and the Profitability Report?',
      answer: 'The P&L Report focuses on revenue, costs, and gross profit for each project. The Profitability Report adds margin variance against budget, giving you a more complete view of financial performance against plan.',
    },
    {
      question: 'What GP calculation method is used?',
      answer: 'The GP calculation method is configured by an Administrator in Settings. The method currently applied is shown in the report. Contact your Administrator if you need to change it.',
    },
    {
      question: 'Why does a project show no profitability data?',
      answer: 'A project will only show profitability data if revenue and cost information has been recorded. If a project is missing data, check that budget and financial entries have been set up in Project Details.',
    },
    {
      question: 'Can I edit financial figures from this report?',
      answer: 'No. The Profitability Report is read-only. To update financial data, navigate to the relevant project\'s billing or budget tabs in Project Details.',
    },
    {
      question: 'How are the profitability health thresholds set?',
      answer: 'Profitability health thresholds are configured by an Administrator in Settings. They define the margin percentage at which a project moves between health statuses.',
    },
  ],

  relatedPages: [
    { label: 'WIP Reporting', path: '/reports/wip' },
    { label: 'Project P&L Report', path: '/reports/project-pl' },
    { label: 'Budget Variance Report', path: '/reports/budget-variance' },
  ],
};
